import { useState } from 'react';
import { salvarCenarioSimuladorSchema, type CenarioSimuladorResumo } from '@triade/contracts';
import {
  useAtualizarCenarioSimulador, useCenariosSimulador, useDuplicarCenarioSimulador, useExcluirCenarioSimulador,
} from '@/api/cenarios-simulador';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import { formatarContagem, formatarDataHora } from '@/lib/format';
import { Vazio } from './vazio';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const CAMPO = 'w-full rounded-[10px] border border-input bg-card px-3 text-body outline-none focus:border-primary focus:shadow-focus';
const TRUNCAR_ANOTACOES = 80;

export function mensagemCenario(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.code ? ` (código ${e.code})` : ''}`;
  return 'Falha de comunicação com a API.';
}

/** Anotacao curta na tabela; o texto completo fica no `title`. Corte de exibicao, nao calculo. */
function anotacaoCurta(texto: string | null): string {
  if (!texto) return '—';
  return texto.length > TRUNCAR_ANOTACOES ? `${texto.slice(0, TRUNCAR_ANOTACOES)}…` : texto;
}

export interface CenariosSalvosProps {
  onAbrir: (id: string) => void;
  onNovo: () => void;
}

/** Listagem dos cenarios salvos do simulador: abre, duplica, edita anotacoes e exclui. A API so devolve o resumo. */
export function CenariosSalvos({ onAbrir, onNovo }: CenariosSalvosProps) {
  const lista = useCenariosSimulador();
  const duplicar = useDuplicarCenarioSimulador();
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState<CenarioSimuladorResumo | null>(null);
  const [excluindo, setExcluindo] = useState<CenarioSimuladorResumo | null>(null);

  async function copiar(c: CenarioSimuladorResumo) {
    setErro(null);
    try {
      await duplicar.mutateAsync({ id: c.id });
    } catch (e) {
      setErro(mensagemCenario(e));
    }
  }

  const cabecalho = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="text-small text-ink-muted">
          Precificar <span aria-hidden="true">/</span> Simulador
        </div>
        <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Simulador de cenários</h1>
        <p className="mt-1 text-ink-secondary">Cenários salvos do simulador. Abra um para continuar ou crie outro.</p>
      </div>
      <Button type="button" onClick={onNovo}>Novo cenário</Button>
    </div>
  );

  let corpo: React.ReactNode;
  if (lista.isPending) {
    corpo = <Vazio papel="status">Carregando cenários salvos…</Vazio>;
  } else if (lista.isError) {
    corpo = (
      <div role="alert" className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-5">
        <p className="text-danger-ink">{mensagemCenario(lista.error)}</p>
        <Button type="button" variant="outline" onClick={() => void lista.refetch()}>Tentar de novo</Button>
      </div>
    );
  } else if (lista.data.itens.length === 0) {
    corpo = (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center">
        <p className="text-small text-ink-muted">Nenhum cenário salvo ainda.</p>
        <Button type="button" onClick={onNovo}>Novo cenário</Button>
      </div>
    );
  } else {
    corpo = (
      <div className="rounded-2xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Item</TableHead>
              <TableHead className="text-right">Cenários</TableHead>
              <TableHead>Anotações</TableHead>
              <TableHead>Quem salvou</TableHead>
              <TableHead>Salvo em / atualizado em</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lista.data.itens.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-semibold">{c.nome}</TableCell>
                <TableCell>{`${c.itemCodigo} · ${c.itemDescricao}`}</TableCell>
                <TableCell className="tabular text-right">{formatarContagem(c.cenarios)}</TableCell>
                <TableCell className="max-w-[260px]">
                  <span className="block truncate text-ink-secondary" title={c.anotacoes ?? undefined}>{anotacaoCurta(c.anotacoes)}</span>
                </TableCell>
                <TableCell>{c.criadoPorNome ?? '—'}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <div>Salvo em {formatarDataHora(c.criadoEm)}</div>
                  <div className="text-caption text-ink-muted">Atualizado em {formatarDataHora(c.atualizadoEm)}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1.5">
                    <Button type="button" size="sm" onClick={() => onAbrir(c.id)} aria-label={`Abrir ${c.nome}`}>Abrir</Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={duplicar.isPending}
                      onClick={() => void copiar(c)}
                      aria-label={`Duplicar ${c.nome}`}
                    >
                      Duplicar
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setEditando(c)} aria-label={`Editar anotações de ${c.nome}`}>
                      Editar anotações
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setExcluindo(c)} aria-label={`Excluir ${c.nome}`}>
                      Excluir
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-4">
      {cabecalho}
      {erro && <p role="alert" className="text-caption text-danger-ink">{erro}</p>}
      {corpo}
      {editando && <EditarAnotacoesDialog key={editando.id} cenario={editando} onFechar={() => setEditando(null)} />}
      {excluindo && <ExcluirCenarioDialog key={excluindo.id} cenario={excluindo} onFechar={() => setExcluindo(null)} />}
    </div>
  );
}

function EditarAnotacoesDialog({ cenario, onFechar }: { cenario: CenarioSimuladorResumo; onFechar: () => void }) {
  const [nome, setNome] = useState(cenario.nome);
  const [anotacoes, setAnotacoes] = useState(cenario.anotacoes ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const atualizar = useAtualizarCenarioSimulador();

  async function confirmar(e: React.FormEvent) {
    e.preventDefault();
    const nomeValido = salvarCenarioSimuladorSchema.shape.nome.safeParse(nome);
    if (!nomeValido.success) {
      setErro(nomeValido.error.issues[0]?.message ?? 'Nome inválido');
      return;
    }
    setErro(null);
    try {
      await atualizar.mutateAsync({ id: cenario.id, nome: nomeValido.data, anotacoes: anotacoes.trim() || null });
      onFechar();
    } catch (err) {
      setErro(mensagemCenario(err));
    }
  }

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onFechar()}>
      <DialogContent>
        <form onSubmit={(e) => void confirmar(e)} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Editar {cenario.nome}</DialogTitle>
            <DialogDescription>Nome com até 80 caracteres e anotações com até 2000.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="cenario-nome-edicao" className={ROTULO}>Nome</Label>
            <Input id="cenario-nome-edicao" maxLength={80} value={nome} onChange={(e) => setNome(e.target.value)} aria-invalid={erro ? true : undefined} />
          </div>
          <div>
            <Label htmlFor="cenario-anotacoes-edicao" className={ROTULO}>Anotações</Label>
            <textarea
              id="cenario-anotacoes-edicao"
              value={anotacoes}
              onChange={(e) => setAnotacoes(e.target.value)}
              maxLength={2000}
              rows={5}
              className={`${CAMPO} py-2`}
            />
          </div>
          {erro && <p role="alert" className="text-caption text-danger-ink">{erro}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onFechar}>Cancelar</Button>
            <Button type="submit" disabled={atualizar.isPending}>Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ExcluirCenarioDialog({ cenario, onFechar }: { cenario: CenarioSimuladorResumo; onFechar: () => void }) {
  const excluir = useExcluirCenarioSimulador();
  const [erro, setErro] = useState<string | null>(null);

  async function confirmar() {
    setErro(null);
    try {
      await excluir.mutateAsync({ id: cenario.id });
      onFechar();
    } catch (e) {
      setErro(mensagemCenario(e));
    }
  }

  return (
    <AlertDialog open onOpenChange={(aberto) => !aberto && onFechar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir {cenario.nome}?</AlertDialogTitle>
          <AlertDialogDescription>O cenário salvo sai da lista.</AlertDialogDescription>
        </AlertDialogHeader>
        {erro && <p role="alert" className="text-caption text-danger-ink">{erro}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <Button type="button" variant="destructive" disabled={excluir.isPending} onClick={() => void confirmar()}>Excluir</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
